import React from 'react';
import states from './../assets/constants/states.json';
import statesCanada from './../assets/constants/statesCanada';
import timezones from './../assets/constants/timezones.json';
import { makeIntArray } from '../utilities/common.jsx';

const createArray = limit => Array.apply(null, { length: limit }).map(Number.call, Number);
const year = new Date().getFullYear();
const getZeroPrefixedOptions = array => {
    let options = [];
    array.forEach(item => {
        let prefixed = item.toString().length === 1 ? `0${item}` : item.toString();
        options.push({ value: prefixed, label: prefixed });
    });
    return options;
};

const usStates = states.map(s => ({ value: s.code, label: s.code }));

export function getTimezones(country = null) {
    if (!country) return timezones;
    return timezones.filter(tz => tz.countries.includes(country));
}

const options = {
    hours: getZeroPrefixedOptions(makeIntArray(12, 1)),
    hours24: getZeroPrefixedOptions(makeIntArray(24, 0)),
    hours16: getZeroPrefixedOptions(makeIntArray(17, 0)),
    minutes: getZeroPrefixedOptions([0, 15, 30, 45]),
    timePeriods: [{ value: 'AM', label: 'AM' }, { value: 'PM', label: 'PM' }],
    days: createArray(31).map(i => ({ value: i + 1, label: i + 1 })),
    months: createArray(12).map(i => ({ value: i + 1, label: i + 1 })),
    monthsNames: [
        {
            value: 1,
            label: 'Jan'
        },
        {
            value: 2,
            label: 'Feb'
        },
        {
            value: 3,
            label: 'Mar'
        },
        {
            value: 4,
            label: 'Apr'
        },
        {
            value: 5,
            label: 'May'
        },
        {
            value: 6,
            label: 'Jun'
        },
        {
            value: 7,
            label: 'Jul'
        },
        {
            value: 8,
            label: 'Aug'
        },
        {
            value: 9,
            label: 'Sep'
        },
        {
            value: 10,
            label: 'Oct'
        },
        {
            value: 11,
            label: 'Nov'
        },
        {
            value: 12,
            label: 'Dec'
        }
    ],
    years: createArray(100).map(i => ({ value: year - i, label: year - i })),
    timezones: timezones,
    genders: [
        { value: 'Male', label: 'Male' },
        { value: 'Female', label: 'Female' },
        { value: 'Other', label: 'Other' },
        { value: 'Decline', label: 'Decline to state' }
    ],
    countries: [{ value: 'US', label: 'US' }, { value: 'CA', label: 'Canada' }],
    states: usStates,
    countryStates: {
        US: usStates,
        CA: statesCanada.map(s => ({ value: s.code, label: s.code }))
    },
    countryStateFieldName: {
        US: { id: 'stateFieldNameUS', defaultMessage: 'State' },
        CA: { id: 'stateFieldNameCA', defaultMessage: 'Province' },
        UK: { id: 'stateFieldNameUK', defaultMessage: 'County' }
    },
    countryPostcodeFieldName: {
        US: { id: 'postcodeFieldNameUS', defaultMessage: 'ZIP' },
        CA: { id: 'postcodeFieldNameCA', defaultMessage: 'Postal Code' },
        UK: { id: 'postcodeFieldNameUK', defaultMessage: 'Postcode' }
    },
    companyTypes: [
        { value: 'individual', label: 'Individual' },
        { value: 'sole_prop', label: 'Sole Proprietorship' },
        { value: 'corporation', label: 'Corporation' },
        { value: 'LLC', label: 'Limited Liability Company (LLC)' },
        { value: 'partnership', label: 'Partnership' },
        { value: 'other', label: 'Other' }
    ],
    companyTypesUK: [{ value: 'selfemployed', label: 'Self-Employed' }, { value: 'LC', label: 'My Own Limited Company' }],
    enrolledOptions: [{ value: 'false', label: 'Awarded' }, { value: 'true', label: 'Enrolled' }],
    contractOptions: [{ value: 'full_time', label: 'Full Time' }, { value: 'part_time', label: 'Part Time' }],

    fedTaxClassTypes: [
        { value: 'Individual/sole proprietor or single-member LLC', label: 'Individual/sole proprietor or single-member LLC' },
        { value: 'C Corporation', label: 'C Corporation' },
        { value: 'S Corporation', label: 'S Corporation' },
        { value: 'Partnership', label: 'Partnership' },
        { value: 'Trust/estate', label: 'Trust/estate' },
        { value: 'Limited liability company', label: 'Limited liability company' }
    ],
    visaTypes: [
        { value: 'F1', label: 'F1' },
        { value: 'J1', label: 'J1' },
        { value: 'B1', label: 'B1' },
        { value: 'B2', label: 'B2' },
        { value: 'L1', label: 'L1' },
        { value: 'H1B', label: 'H1B' }
    ],
    filingStatus: [
        { value: 'single', label: 'Single' },
        { value: 'married-jointly', label: 'Married filing jointly' },
        { value: 'married-separately', label: 'Married filing separately' }
    ],
    getClassificationPrefs: admin => [
        { value: 'w2-or-ic', label: `Determined by ${admin && admin.name}` },
        { value: 'w2-only', label: `Employed by ${admin && admin.name} only` },
        { value: 'ic-preferred', label: `Independent Contractor if possible, else ${admin && admin.name} employee` },
        { value: 'ic-only', label: `Independent Contractor only` }
    ],
    workerClassification: [{ value: 'w2', label: 'EE' }, { value: 'ic', label: 'IC' }],
    purchaseOrderStatus: [{ value: 'now', label: 'Available' }, { value: 'later', label: 'In process' }, { value: 'never', label: 'No PO required' }],
    getSignatureFonts: (name, chosenFont) =>
        Array.apply(null, { length: 6 }).map((_, i) => {
            let index = i + 1;
            return {
                value: index,
                label: (
                    <div className={`font-option signature-font-${index} no-select col-sm-6 col-xs-12`}>
                        <i className="material-icons">{chosenFont === index.toString() ? 'check_circle_outline' : 'radio_button_unchecked'}</i>
                        <span>{name}</span>
                    </div>
                )
            };
        }),
    newStarterInfoQuestions: {
        studentLoanPlan: {
            answer_type: 'y/n',
            id: 'studentLoanPlan',
            title: 'Do you have a Student Loan Plan which is not fully repaid?',
            help_text: getStudentLoanHelpText()
        },
        studiesCompletion: {
            answer_type: 'y/n',
            id: 'studiesCompletion',
            title: 'Did you complete or leave your studies before 6th April?'
        },
        creditCardRepaying: {
            answer_type: 'y/n',
            id: 'creditCardRepaying',
            title: 'Are you repaying your Student Loan directly to the Student Loans Company by direct debit?'
        },
        studentLoanType: {
            answer_type: 'select',
            attributes: {
                placeHolder: 'Plan',
                choices: ['Plan1', 'Plan 2', 'Both']
            },
            id: 'studentLoanType',
            title: 'What type of Student Loan do you have?',
            help_text: getStudentLoanHelpText()
        },
        postgraduateLoan: {
            answer_type: 'y/n',
            id: 'postgraduateLoan',
            title: 'Do you have a Postgraduate Loan which is not fully repaid?',
            help_text: getPostgraduateLoanHelpText()
        },
        postgraduateCompletion: {
            answer_type: 'y/n',
            id: 'postgraduateCompletion',
            title: 'Did you complete or leave your Postgraduate studies before 6th April?'
        },
        postgraduateCreditCardRepaying: {
            answer_type: 'y/n',
            id: 'postgraduateCreditCardRepaying',
            title: 'Are you repaying your Postgraduate Loan directly to the Student Loans Company by direct debit?'
        }
    }
};

export default options;

export function getClassificationPrefName(admin, value) {
    const prefOptions = options.getClassificationPrefs(admin);
    const selectedOption = prefOptions.find(opt => opt.value === value);
    return selectedOption && selectedOption.label;
}

function getStudentLoanHelpText() {
    return `
            <div>
                The following are the Student Loan Plans that apply in the following scenarios:
                <br />
                <br />
                You’ll have a Plan 1 Student Loan if:
                <ul>
                    <li>you lived in Scotland or Northern Ireland when you started your course (undergraduate or postgraduate)</li>
                    <li>you lived in England or Wales and started your undergraduate course before 1 September 2012 </li>
                </ul>
                <br />
                You’ll have a Plan 2 Student Loan if:
                <ul>
                    <li>you lived in England or Wales and started your undergraduate course on or after 1 September 2012 </li>
                    <li>your loan is a Part Time Maintenance Loan </li>
                    <li>your loan is an Advanced Learner Loan </li>
                    <li>your loan is a Postgraduate Healthcare Loan</li>
                </ul>
            </div>`;
}

function getPostgraduateLoanHelpText() {
    return `
            <div>
                You’ll have a Postgraduate Loan if:
                <br />
                <br />
                <ul>
                    <li>you lived in England and started your Postgraduate Master’s course on or after 1 August 2016</li>
                    <li>you lived in Wales and started your Postgraduate Master’s course on or after 1 August 2017</li>
                    <li>you lived in England or Wales and started your Postgraduate Doctoral course on or after 1 August 2018</li>
                </ul>
            </div>`;
}



// WEBPACK FOOTER //
// ./src/text/selectOptions.js